import React from 'react';
import { Card, Container, Divider, Stack } from '@fiverr-private/layout_components';
import { CurrentPlanBadge, RecommendedBadge } from '@fiverr-private/badges';
import { Heading, Text } from '@fiverr-private/typography';
import { CheckIcon } from '@fiverr-private/visuals';
import { I18n } from '@fiverr-private/i18n-react';
import { AppI18n } from '../../utils/i18n';
import { CompliancePackageData } from '../../types/compliance';
import { centsToCurrency, DEFAULT_CURRENCY } from '../../utils/currency';
import { Currency } from '../../types';
import { formatFullDateShort } from '../../utils/date';

export interface BasePlanCardProps {
    planType: 'essential' | 'advanced';
    badge?: 'current' | 'recommended';
    compliancePackageData: CompliancePackageData;
    advancedPlanPrice?: number;
    endDate?: string;
    currency?: Currency;
    containerProps?: React.ComponentProps<typeof Card>;
    isUsSeller?: boolean; // US sellers have different background check price
}

export type PlanCardProps = Omit<BasePlanCardProps, 'planType'>;

export const BasePlanCard = ({
    planType,
    badge,
    compliancePackageData,
    endDate,
    advancedPlanPrice,
    isUsSeller,
    currency,
    containerProps,
}: BasePlanCardProps) => {
    // Compliance prices come from BE. The default object relies on the price being formatted and rate applied to it, hence the rate is 1
    const currencyObject: Currency = {
        name: compliancePackageData?.legalDocsPackageData?.currency || DEFAULT_CURRENCY,
        rate: 1,
    };

    const planEndDate = formatFullDateShort(endDate || '');
    const legalDocsPrice = centsToCurrency(compliancePackageData?.legalDocsPackageData?.price, currencyObject);
    const backgroundCheckPrice = centsToCurrency(
        isUsSeller
            ? compliancePackageData?.backgroundCheckPackageData?.usPrice
            : compliancePackageData?.backgroundCheckPackageData?.nonUsPrice,
        currencyObject
    );
    const wfcPrice = centsToCurrency(compliancePackageData?.workforceClassificationPackageData?.price, currencyObject);

    return (
        <Card padding="4" width={{ default: '100%', md: '370px' }} borderRadius="lg" {...containerProps}>
            <Stack justifyContent="flexEnd" width="100%" height="20px">
                {badge === 'current' && <CurrentPlanBadge />}
                {badge === 'recommended' && <RecommendedBadge />}
            </Stack>

            <Container padding="2">
                <Heading size="h_md" paddingBottom="2">
                    <AppI18n k={`plan_description_cards.${planType}.title`}></AppI18n>
                </Heading>

                <Text size="b_sm" color="bodySecondary" paddingBottom="4">
                    <AppI18n k={`plan_description_cards.${planType}.subtitle`}></AppI18n>
                </Text>
                <Text>
                    <AppI18n
                        k={`plan_description_cards.${planType}.details`}
                        params={{
                            price: centsToCurrency(advancedPlanPrice || 0, currency),
                            date: planEndDate,
                        }}
                    ></AppI18n>
                </Text>

                <Divider marginY="4" />

                <Stack gap="3" direction="column">
                    <Text fontWeight="semibold" size="b_sm">
                        <AppI18n k={`plan_description_cards.${planType}.pricing_title`}></AppI18n>
                    </Text>
                    <Text size="b_sm">
                        <Stack gap="4">
                            <Container width="16px">
                                <CheckIcon size="sm" />
                            </Container>
                            <I18n
                                k={`plan_change.plan_description_cards.${planType}.legal_docs_price`}
                                params={{ price: legalDocsPrice }}
                            ></I18n>
                        </Stack>
                    </Text>
                    <Text size="b_sm">
                        <Stack gap="4">
                            <Container width="16px">
                                <CheckIcon size="sm" />
                            </Container>
                            <I18n
                                k={`plan_change.plan_description_cards.${planType}.background_price`}
                                params={{ price: backgroundCheckPrice }}
                            ></I18n>
                        </Stack>
                    </Text>
                    <Text size="b_sm">
                        <Stack gap="4">
                            <Container width="16px">
                                <CheckIcon size="sm" />
                            </Container>
                            <I18n
                                k={`plan_change.plan_description_cards.${planType}.wfc_price`}
                                params={{ price: wfcPrice }}
                            ></I18n>
                        </Stack>
                    </Text>
                </Stack>
            </Container>
        </Card>
    );
};
